import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClock,
  FiTarget,
  FiTrendingUp,
  FiZap,
  FiBookOpen,
  FiCalendar,
  FiCheckCircle,
  FiArrowRight,
  FiAlertTriangle,
  FiPlusCircle,
  FiX,
} from 'react-icons/fi';

// High-priority strategy tips that will become lessons
const HIGH_PRIORITY_TIPS = [
  {
    id: 'time-management',
    title: 'Master Time Management',
    description:
      'Learn to allocate time strategically across different question types',
    icon: <FiClock />,
    priority: 'Essential',
    estimatedTime: '15 min',
    isLesson: true,
    benefits: [
      'Finish all questions within time limit',
      'Reduce test anxiety from time pressure',
      'Maximize your score potential',
      'Develop consistent pacing strategy',
    ],
  },
  {
    id: 'question-triage',
    title: 'Question Triage Strategy',
    description:
      'Quickly identify easy, medium, and hard questions to optimize your approach',
    icon: <FiTarget />,
    priority: 'Essential',
    estimatedTime: '12 min',
    isLesson: true,
    benefits: [
      'Answer easy questions quickly and accurately',
      'Avoid getting stuck on difficult problems',
      'Build confidence and momentum',
      'Maximize points per minute invested',
    ],
  },
  {
    id: 'estimation-techniques',
    title: 'Strategic Estimation',
    description: 'Use approximation and elimination to solve problems faster',
    icon: <FiZap />,
    priority: 'High',
    estimatedTime: '10 min',
    isLesson: true,
    benefits: [
      'Solve problems in 30-50% less time',
      'Eliminate obviously wrong answers',
      'Check your work quickly',
      'Reduce calculation errors',
    ],
  },
  {
    id: 'question-type-playbook',
    title: 'Question Type Playbook',
    description:
      'Choose the right strategy for QC, single choice, multiple select, and numeric entry questions',
    icon: <FiBookOpen />,
    priority: 'Essential',
    estimatedTime: '14 min',
    isLesson: true,
    benefits: [
      'Handle every official Quant question format',
      'Avoid multiple-select and numeric-entry traps',
      'Use QC-specific comparison tactics',
      'Match your solving method to the answer format',
    ],
  },
  {
    id: 'calculator-strategies',
    title: 'Calculator Strategy',
    description:
      'Use the GRE calculator only when it saves time and reduces arithmetic risk',
    icon: <FiZap />,
    priority: 'High',
    estimatedTime: '10 min',
    isLesson: true,
    benefits: [
      'Decide when mental math is faster',
      'Reduce calculator entry mistakes',
      'Preserve the 105-second pacing target',
      'Use estimation before exact calculation',
    ],
  },
  {
    id: 'section-adaptive-scoring',
    title: 'Section-Adaptive Scoring',
    description:
      'Understand how Section 1 performance influences the difficulty and scoring ceiling of Section 2',
    icon: <FiTrendingUp />,
    priority: 'Essential',
    estimatedTime: '12 min',
    isLesson: true,
    benefits: [
      'Protect accuracy in the first Quant section',
      'Interpret harder second-section practice correctly',
      'Make scoring-aware skip decisions',
      'Review results by section difficulty',
    ],
  },
];

// General strategy tips (not lessons)
const STRATEGY_TIPS = [
  {
    category: 'Problem-Solving Approach',
    icon: <FiTarget />,
    tips: [
      {
        title: 'Read Questions Carefully',
        content:
          'Pay attention to what the question is actually asking. Look for keywords like "approximately," "could be," "must be."',
        importance: 'high',
      },
      {
        title: 'Work Backwards',
        content:
          'For multiple choice questions, try plugging answer choices into the problem, starting with middle values.',
        importance: 'medium',
      },
      {
        title: 'Draw Diagrams',
        content:
          'Visual representation often makes geometry and word problems much clearer.',
        importance: 'high',
      },
      {
        title: 'Check Units',
        content:
          'Ensure your final answer has the correct units (dollars, square meters, etc.).',
        importance: 'medium',
      },
    ],
  },
  {
    category: 'Time Optimization',
    icon: <FiClock />,
    tips: [
      {
        title: '1.5-2 Minutes Per Question',
        content:
          'Aim for 90-120 seconds per question on average. Some will be faster, others slower.',
        importance: 'high',
      },
      {
        title: 'Skip and Return',
        content:
          'If a question takes over 3 minutes, mark it and move on. Return if time permits.',
        importance: 'high',
      },
      {
        title: 'Use the Calculator Wisely',
        content:
          'The on-screen calculator is helpful but can be slow. Do simple arithmetic mentally.',
        importance: 'medium',
      },
      {
        title: 'Watch the Clock',
        content:
          'Check time every 5-7 questions. Aim to complete 10 questions in 17-20 minutes.',
        importance: 'high',
      },
    ],
  },
  {
    category: 'Answer Strategy',
    icon: <FiCheckCircle />,
    tips: [
      {
        title: 'Eliminate Impossible Answers',
        content:
          'Cross out clearly wrong answers first. Often you can eliminate 2-3 choices immediately.',
        importance: 'high',
      },
      {
        title: 'Use Answer Choice Clues',
        content:
          'Answer choices give hints about the expected form (fraction, decimal, etc.).',
        importance: 'medium',
      },
      {
        title: 'Trust Your First Instinct',
        content:
          'When unsure between two answers, your first educated guess is often correct.',
        importance: 'medium',
      },
      {
        title: 'Never Leave Blanks',
        content:
          "There's no penalty for wrong answers. Always guess if you're running out of time.",
        importance: 'high',
      },
    ],
  },
  {
    category: 'Mental Preparation',
    icon: <FiTrendingUp />,
    tips: [
      {
        title: 'Practice Under Pressure',
        content:
          'Simulate test conditions with time limits and minimal breaks.',
        importance: 'high',
      },
      {
        title: 'Build Confidence',
        content:
          'Start each section with easier questions to build momentum and confidence.',
        importance: 'medium',
      },
      {
        title: 'Stay Calm on Hard Questions',
        content:
          "Remember that some questions are designed to be very difficult. Don't panic.",
        importance: 'medium',
      },
      {
        title: 'Review Fundamentals',
        content:
          'Before the test, quickly review basic formulas and common question types.',
        importance: 'high',
      },
    ],
  },
];

const QuantStrategy = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAllTips, setShowAllTips] = useState(true);
  const [completedLessons, setCompletedLessons] = useState(new Set());

  const handleLessonStart = (lessonId) => {
    navigate(`/exams/gre/strategy/${lessonId}`);
  };

  const renderPriorityLesson = (lesson) => (
    <div
      key={lesson.id}
      style={{
        background: 'var(--liwaza-surface-container-highest)',
        border: '2px solid var(--liwaza-primary)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Priority Badge */}
      <div
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background:
            lesson.priority === 'Essential'
              ? 'var(--liwaza-error)'
              : 'var(--liwaza-tertiary)',
          color: 'white',
          padding: '0.25rem 0.5rem',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: '600',
        }}
      >
        {lesson.priority}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            padding: '0.75rem',
            borderRadius: '8px',
            fontSize: '1.25rem',
          }}
        >
          {lesson.icon}
        </div>
        <div style={{ flex: 1 }}>
          <h3
            style={{
              color: 'var(--liwaza-primary)',
              marginBottom: '0.5rem',
              fontSize: '1.25rem',
              fontWeight: '600',
            }}
          >
            {lesson.title}
          </h3>
          <p
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              marginBottom: '1rem',
              lineHeight: 1.5,
            }}
          >
            {lesson.description}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              fontSize: '0.9rem',
              color: 'var(--liwaza-on-surface-variant)',
            }}
          >
            <span
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <FiClock /> {lesson.estimatedTime}
            </span>
            {completedLessons.has(lesson.id) && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: 'var(--liwaza-primary)',
                }}
              >
                <FiCheckCircle /> Completed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '0.5rem',
            fontSize: '0.95rem',
            fontWeight: '600',
          }}
        >
          What you'll learn:
        </h4>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '0.5rem',
          }}
        >
          {lesson.benefits.map((benefit, index) => (
            <li
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                color: 'var(--liwaza-on-surface-variant)',
              }}
            >
              <FiCheckCircle
                style={{ color: 'var(--liwaza-primary)', fontSize: '0.8rem' }}
              />
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      {/* Action Button */}
      <button
        onClick={() => handleLessonStart(lesson.id)}
        style={{
          background: completedLessons.has(lesson.id)
            ? 'var(--liwaza-secondary)'
            : 'var(--liwaza-primary)',
          color: 'white',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s',
        }}
      >
        {completedLessons.has(lesson.id) ? 'Review Lesson' : 'Start Lesson'}
        <FiArrowRight />
      </button>
    </div>
  );

  const renderTipCategory = (category) => (
    <div
      key={category.category}
      style={{
        background: 'var(--liwaza-surface-container)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1rem',
      }}
    >
      <h3
        style={{
          color: 'var(--liwaza-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          fontSize: '1.1rem',
          fontWeight: '600',
        }}
      >
        {category.icon}
        {category.category}
      </h3>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {category.tips.map((tip, index) => (
          <div
            key={index}
            style={{
              background: 'var(--liwaza-surface-container-highest)',
              padding: '1rem',
              borderRadius: '8px',
              borderLeft: `4px solid ${tip.importance === 'high' ? 'var(--liwaza-primary)' : 'var(--liwaza-secondary)'}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}
            >
              <h4
                style={{
                  color: 'var(--liwaza-on-surface)',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  margin: 0,
                }}
              >
                {tip.title}
              </h4>
              {tip.importance === 'high' && (
                <span
                  style={{
                    background: 'var(--liwaza-primary)',
                    color: 'white',
                    padding: '0.15rem 0.4rem',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}
                >
                  HIGH PRIORITY
                </span>
              )}
            </div>
            <p
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {tip.content}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <FiTarget /> GRE Quantitative: Timing & Strategy
        </h1>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '1.1rem',
            lineHeight: 1.5,
          }}
        >
          Master essential strategies and time management techniques to maximize
          your GRE Quant score. Start with high-priority lessons, then explore
          additional tips.
        </p>
      </div>

      {/* High-Priority Strategy Lessons */}
      <div style={{ marginBottom: '3rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem',
          }}
        >
          <h2
            style={{
              color: 'var(--liwaza-primary)',
              fontSize: '1.4rem',
              fontWeight: '600',
              margin: 0,
            }}
          >
            Essential Strategy Lessons
          </h2>
          <div
            style={{
              background: 'var(--liwaza-error)',
              color: 'white',
              padding: '0.25rem 0.5rem',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: '600',
            }}
          >
            START HERE
          </div>
        </div>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            marginBottom: '1.5rem',
            fontSize: '1rem',
          }}
        >
          These interactive lessons cover the most impactful strategies for
          improving your Quant score. Complete them in order for maximum
          benefit.
        </p>

        {HIGH_PRIORITY_TIPS.map(renderPriorityLesson)}
      </div>

      {/* Quick Strategy Tips */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
          }}
        >
          <h2
            style={{
              color: 'var(--liwaza-primary)',
              fontSize: '1.4rem',
              fontWeight: '600',
              margin: 0,
            }}
          >
            Quick Strategy Tips
          </h2>
          <button
            onClick={() => setShowAllTips(!showAllTips)}
            style={{
              background: 'var(--liwaza-secondary)',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {showAllTips ? 'Show Less' : 'Show All Categories'}
            {showAllTips ? <FiX /> : <FiPlusCircle />}
          </button>
        </div>

        {showAllTips ? (
          STRATEGY_TIPS.map(renderTipCategory)
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem',
            }}
          >
            {STRATEGY_TIPS.slice(0, 2).map((category) => (
              <div
                key={category.category}
                style={{
                  background: 'var(--liwaza-surface-container)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '2px solid transparent',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderColor = 'var(--liwaza-primary)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderColor = 'transparent')
                }
                onClick={() => setShowAllTips(true)}
              >
                <h3
                  style={{
                    color: 'var(--liwaza-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                  }}
                >
                  {category.icon}
                  {category.category}
                </h3>
                <p
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    fontSize: '0.9rem',
                    marginBottom: '1rem',
                  }}
                >
                  {category.tips.length} essential tips
                </p>
                <div
                  style={{
                    color: 'var(--liwaza-primary)',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}
                >
                  Click to view all tips <FiArrowRight />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuantStrategy;
